<template>
  <div
    class="makercrate-item-maker-cart"
    @click="$emit('open-detail', item.id)"
    @keydown.enter="$emit('open-detail', item.id)"
    @keydown.space.prevent="$emit('open-detail', item.id)"
    role="button"
    tabindex="0"
    :aria-label="`View details for ${item.title}`"
  >
    <div class="makercrate-item-content">
      <!-- Header with Badge and Heart Icon -->
      <div class="makercrate-header">
        <div class="makercrate-badge-container">
          <MakeyMakeyBadge :show="item.hasMakeyBadge" />
          <!-- Empty placeholder to keep layout consistent when no badge -->
          <div v-if="!item.hasMakeyBadge" style="height: 32px" />
        </div>
        <div class="makercrate-heart-container">
          <svg
            width="22"
            height="20"
            viewBox="0 0 22 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2.66275 11.7111L8.82377 18.2042C10.0068 19.4509 11.9932 19.4509 13.1762 18.2042L19.3372 11.7111C21.5542 9.37467 21.5543 5.58651 19.3373 3.25003C17.1203 0.913562 13.5258 0.913563 11.3088 3.25004C11.1409 3.42693 10.8591 3.42693 10.6912 3.25004C8.47421 0.913563 4.87975 0.913563 2.66275 3.25003C0.44575 5.58651 0.445751 9.37467 2.66275 11.7111Z"
              stroke="#E00069"
              stroke-width="1.5"
            />
          </svg>
        </div>
      </div>

      <!-- Image Grid -->
      <div class="makercrate-image-grid">
        <div class="makercrate-image-card">
          <img
            :src="item.primaryImage"
            alt="Primary kit image"
            class="makercrate-image"
            @error="handleImageError"
          />
        </div>
        <div class="makercrate-image-card">
          <img
            :src="item.secondaryImage"
            alt="Secondary kit image"
            class="makercrate-image"
            @error="handleImageError"
          />
        </div>
      </div>

      <!-- Hover Details Section (slide-up panel) -->
      <div class="makercrate-details-container">
        <div class="makercrate-details-content">
          <!-- Title Section -->
          <div class="makercrate-title-section">
            <h3 class="makercrate-main-title">{{ item.title }}</h3>
            <p class="makercrate-price">${{ item.price }}</p>
          </div>

          <div class="makercrate-divider"></div>

          <div class="makercrate-description">
            <p class="makercrate-description-text">
              {{ item.description || "" }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable */
import { defineProps, defineEmits } from "vue";
import MakeyMakeyBadge from "./MakeyMakeyBadge.vue";

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(["open-detail"]);

const handleImageError = (event) => {
  event.target.src = "/images/content-placeholder.svg";
};
</script>

<style scoped>
.makercrate-item-maker-cart {
  background-color: #f2f3fb;
  border-radius: 16px;
  border: 1px solid #d8dae5;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  transition: transform 0.3s ease;
  height: 450px;
  display: flex;
  flex-direction: column;
}

.makercrate-item-maker-cart:hover {
  transform: translateY(-5px);
}

.makercrate-item-content {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  height: 100%;
  position: relative;
}

.makercrate-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.makercrate-badge-container {
  display: flex;
  align-items: center;
  flex: 1;
}

/* Badge styles moved to MakeyMakeyBadge.vue */

.makercrate-heart-container {
  position: relative;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.makercrate-heart-container:hover {
  transform: scale(1.1);
}

.makercrate-image-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  height: 271px;
  width: 100%;
}

.makercrate-image-card {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #d8dae5;
}

.makercrate-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

/* Hover slide-up details panel */
.makercrate-details-container {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  padding-bottom: 40px;
  height: 180px;
  z-index: 3;
  background-color: #f2f3fb;
  border-radius: 0 0 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  transform: translateY(calc(100% - 120px));
  transition:
    transform 0.4s ease,
    padding-bottom 0.4s ease;
}

.makercrate-item-maker-cart:hover .makercrate-details-container {
  transform: translateY(0px);
  padding-bottom: 16px;
}

.makercrate-details-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.makercrate-title-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.makercrate-main-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-xxl);
  line-height: 36px;
  color: var(--primary-blue);
  margin: 0;
  word-break: break-word;
}

.makercrate-price {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-large);
  line-height: 28px;
  color: var(--primary-blue);
  margin: 0;
}

.makercrate-divider {
  width: 100%;
  height: 1px;
  border-bottom: 2px dashed #98a2b3;
  margin: 4px 0;
  opacity: 0;
}

.makercrate-item-maker-cart:hover .makercrate-divider {
  opacity: 1;
}

.makercrate-description {
  opacity: 0;
  transform: translateY(20px);
  transition:
    opacity 0.4s ease,
    transform 0.4s ease;
  transition-delay: 0.2s;
}

.makercrate-item-maker-cart:hover .makercrate-description {
  opacity: 1;
  transform: translateY(0);
}

.makercrate-description-text {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-small);
  line-height: 20px;
  letter-spacing: 0.014px;
  color: #3b4648;
  margin: 0;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  padding-right: 12px;
  text-overflow: ellipsis;
}

/* Responsive Design */
@media (max-width: 768px) {
  .makercrate-item-maker-cart {
    height: 400px;
  }

  .makercrate-item-content {
    padding: 12px;
    gap: 8px;
  }

  .makercrate-image-grid {
    height: 180px;
    gap: 6px;
  }

  .makercrate-main-title {
    font-size: var(--fs-xl);
    line-height: 32px;
  }

  .makercrate-price {
    font-size: 18px;
    line-height: 24px;
  }

  .makercrate-details-container {
    height: 200px;
    padding: 12px;
    padding-bottom: 30px;
    transform: translateY(calc(100% - 60px)) !important;
  }

  .makercrate-description-text {
    font-size: 13px;
    line-height: 18px;
  }

  /* Disable hover transitions on mobile */
  .makercrate-item-maker-cart:hover {
    transform: none;
  }

  .makercrate-item-maker-cart:hover .makercrate-divider {
    opacity: 0;
  }

  .makercrate-item-maker-cart:hover .makercrate-description {
    opacity: 0;
    transform: translateY(20px);
  }

  .makercrate-heart-container:hover {
    transform: none;
  }
}

@media (max-width: 480px) {
  .makercrate-item-maker-cart {
    height: 300px;
  }

  .makercrate-item-content {
    padding: 10px;
  }

  .makercrate-image-grid {
    height: 170px;
  }

  .makercrate-main-title {
    font-size: var(--fs-big);
    line-height: 28px;
  }

  .makercrate-price {
    font-size: var(--fs-medium);
    line-height: 22px;
  }

  .makercrate-details-container {
    height: 180px;
    padding: 10px;
    padding-bottom: 25px;
    transform: translateY(calc(100% - 80px)) !important;
  }

  .makercrate-description-text {
    font-size: 12px;
    line-height: 16px;
  }

  /* Disable hover transitions on smaller mobile */
  .makercrate-item-maker-cart:hover {
    transform: none;
  }

  .makercrate-item-maker-cart:hover .makercrate-divider {
    opacity: 0;
  }

  .makercrate-item-maker-cart:hover .makercrate-description {
    opacity: 0;
    transform: translateY(20px);
  }

  .makercrate-heart-container:hover {
    transform: none;
  }
}
</style>
